import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VaultPage from './pages/vault/VaultPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { VaultProvider } from './contexts/VaultContext';
import { useAppSelector } from './hooks/useRedux';
import { Toaster } from 'react-hot-toast';
import FavoritePage from './pages/vault/FavoritePage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import RecoveryPage from './pages/auth/RecoveryPage';
import AccountUnlockPage from './pages/auth/AccountUnlockPage';
import SharingCenterPage from './pages/vault/SharingCenterPage';
import CategoryPage from './pages/vault/CategoriesPage';
import { CategoryProvider } from './contexts/CategoryContext';
import FeaturesPage from './pages/FeaturesPage';
import LandingPage from './pages/LandingPage';
import VaultHeader from './components/vault/VaultHeader';
import MfaSetup from './components/settings/MfaSetup';
import NotesPage from './pages/notes/NotesPage';
import PersonalDetailsPage from './pages/personalDetails/PersonalDetailsPage';
import CardDetailsPage from './pages/cardDetails/CardDetailsPage';
import PasswordsPage from './pages/passwords/PasswordsPage';
import BankDetailsPage from './pages/bankDetails/BankDetailsPage';
import ImportPage from './pages/import/ImportPage';
import RolesPage from './pages/roles/RolesPage';
import TeamMembersPage from './pages/team/TeamMembersPage';
import { SidebarProvider } from './contexts/SidebarContext';
import { AuthProvider } from './contexts/AuthContext';
import RegisterGuard from './components/auth/RegisterGuard';
import PermissionProtectedRoute from './components/auth/PermissionProtectedRoute';
import NotAuthorizedPage from './pages/errors/NotAuthorizedPage';
import TeamsPage from './pages/team/TeamsPage';
import TeamDetailsPage from './pages/team/TeamDetailsPage';
import AcceptInvitation from './pages/AcceptInvitation';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsConditions from './pages/legal/TermsConditions';
import Navbar from './components/common/Navbar';
import { MaintenanceProvider, useMaintenanceContext } from './contexts/MaintenanceContext';
import MaintenancePage from './components/MaintenancePage';
import SettingsPage from './pages/settings/SettingsPage';
import { ThemeProvider } from './contexts/ThemeContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import OnboardingTour from './components/onboarding/OnboardingTour';
import { useOnboarding } from './contexts/OnboardingContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import PricingPage from './pages/PricingPage';
import OrganizationSignup from './components/auth/OrganizationSignup';
import TrialBanner from './components/common/TrialBanner';
import PaymentSuccess from './components/payment/PaymentSuccess';
import PaymentError from './components/payment/PaymentError';
import TransactionsPage from './pages/team/TransactionsPage';
import SecurityPage from './pages/SecurityPage';
import AboutPage from './pages/AboutPage';
import DownloadPage from './pages/DownloadPage';
import ContactPage from './pages/ContactPage';
import AuditDashboardPage from './pages/admin/AuditDashboardPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminCategoryItemsPage from './pages/admin/AdminUserItemsPage';

// Separate component for routes that uses Redux hooks
const AppRoutes: React.FC = () => {
    const { user } = useAppSelector((state) => state.auth);
    const { isFirstTimeUser, completeOnboarding } = useOnboarding();

    return (
        <AuthProvider>
            <ThemeProvider>
                <WorkspaceProvider>
                    <VaultProvider>
                        <SidebarProvider>
                            <CategoryProvider>
                                {/* {user && <TrialBanner />} */}
                                <Routes>
                                    {/* Public Routes */}
                                    <Route path="/" element={<LandingPage />} />
                                    <Route path="/features" element={<FeaturesPage />} />
                                    <Route path="/pricing" element={<PricingPage />} />
                                    <Route path="/about" element={<AboutPage />} />
                                    <Route path="/security" element={<SecurityPage />} />
                                    <Route path="/download" element={<DownloadPage />} />
                                    <Route path="/contact" element={<ContactPage />} />
                                    <Route path="/organization/signup" element={<OrganizationSignup />} />
                                    <Route path="/not-authorized" element={<NotAuthorizedPage />} />
                                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                    <Route path="/terms-of-service" element={<TermsConditions />} />

                                    <Route
                                        path="/login"
                                        element={user ? <Navigate to="/all-items" replace /> : <LoginPage />}
                                    />
                                    <Route
                                        path="/register"
                                        element={
                                            <RegisterGuard>
                                                <RegisterPage />
                                            </RegisterGuard>
                                        }
                                    />


                                    {/* Protected Routes */}
                                    <Route path="/all-items" element={
                                        <ProtectedRoute>
                                            <VaultPage />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/passwords" element={
                                        <ProtectedRoute>
                                            <PasswordsPage />
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/favorites" element={
                                        <ProtectedRoute>
                                            <FavoritePage />
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/vaults" element={
                                        <ProtectedRoute>
                                            <CategoryPage />
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/sharing-center" element={
                                        <ProtectedRoute>
                                            <SharingCenterPage />
                                        </ProtectedRoute>
                                    } />

                                        <Route path="/settings" element={
                                            <ProtectedRoute>
                                                <SettingsPage />
                                            </ProtectedRoute>
                                        } />

                                    <Route path="/roles" element={
                                        <PermissionProtectedRoute requiredCategory="role_management" requiredPermission="view_roles">
                                            <RolesPage />
                                        </PermissionProtectedRoute>
                                    } />

                                    <Route path="/audit" element={
                                        <PermissionProtectedRoute requiredCategory="role_management" requiredPermission="view_roles">
                                            <AuditDashboardPage />
                                        </PermissionProtectedRoute>
                                    } />

                                    <Route path="/manage-members" element={
                                        <PermissionProtectedRoute requiredCategory="team" requiredPermission="view_team_members">
                                            <TeamMembersPage />
                                        </PermissionProtectedRoute>
                                    } />
                                    
                                    <Route path="/admin/user/:userId/categories" element={
                                        <ProtectedRoute>
                                            <AdminCategoriesPage />
                                        </ProtectedRoute>
                                    } />
                                    
                                    <Route path="/admin/user/:userId/category/:categoryId" element={
                                        <ProtectedRoute>
                                            <AdminCategoryItemsPage />
                                        </ProtectedRoute>
                                    } />

                                    <Route
                                        path="/personal-details"
                                        element={
                                            <ProtectedRoute>
                                                <PersonalDetailsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/card-details"
                                        element={
                                            <ProtectedRoute>
                                                <CardDetailsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/bank-details"
                                        element={
                                            <ProtectedRoute>
                                                <BankDetailsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/import"
                                        element={
                                            <ProtectedRoute >
                                                <ImportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    {/* <Route path="/notes" element={
                                        <ProtectedRoute>
                                            <NotesPage />
                                        </ProtectedRoute>
                                    } /> */}

                                    <Route path="/mfa-setup" element={
                                        <ProtectedRoute>
                                            <MfaSetup />
                                        </ProtectedRoute>
                                    } />

                                    <Route
                                        path="/teams"
                                        element={
                                            <PermissionProtectedRoute requiredCategory="team" requiredPermission="view_teams">
                                                <TeamsPage />
                                            </PermissionProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/team/:teamId"
                                        element={
                                            <ProtectedRoute>
                                                <TeamDetailsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/team/:teamId/transactions"
                                        element={
                                            <ProtectedRoute>
                                                <TransactionsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Redirect root to appropriate page */}
                                    <Route
                                        path="/"
                                        element={<Navigate to={user ? "/vaults" : "/"} replace />}
                                    />

                                    {/* 404 Page */}
                                    <Route path="*" element={
                                        <div>
                                            <Navbar />
                                            <div className="min-h-screen flex items-center justify-center">
                                                <div className="text-center">
                                                    <h1 className="text-4xl font-bold text-gray-900">404</h1>
                                                    <p className="text-gray-600">Page not found</p>
                                                </div>
                                            </div>
                                        </div>

                                    } />
                                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                    <Route path="/account-recovery" element={<RecoveryPage />} />
                                    <Route path="/account/unlock" element={<AccountUnlockPage />} />
                                    <Route path="/not-authorized" element={<NotAuthorizedPage />} />
                                    <Route path="/accept-invitation" element={<AcceptInvitation />} />
                                    <Route path="/payment/success" element={<PaymentSuccess />} />
                                    <Route path="/payment/error" element={<PaymentError />} />
                                </Routes>
                                    <OnboardingTour 
                                        isFirstTimeUser={isFirstTimeUser} 
                                        onComplete={completeOnboarding} 
                                    />
                            </CategoryProvider>
                        </SidebarProvider>
                    </VaultProvider>
                </WorkspaceProvider>
            </ThemeProvider>
        </AuthProvider>
    );
};

// Create a maintenance wrapper component
const MaintenanceWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isInMaintenance, maintenanceMessage } = useMaintenanceContext();
    
    if (isInMaintenance) {
        return <MaintenancePage message={maintenanceMessage} />;
    }
    
    return <>{children}</>;
};

// Main App component that provides Redux store
const App: React.FC = () => {
    // Initialize extension sync on mount
    React.useEffect(() => {
        // Dynamic import to avoid issues if not in browser
        import('./utils/extensionSync').then(({ initializeExtensionSync }) => {
            initializeExtensionSync();
            console.log('Extension sync initialized in App');
        }).catch((error) => {
            console.log('Extension sync not available:', error);
        });
    }, []);

    return (
        <Provider store={store}>
            <PersistGate loading={<div>Loading...</div>} persistor={persistor}>
                <OnboardingProvider>
                    <AuthProvider>
                        <MaintenanceProvider>
                            <MaintenanceWrapper>
                                <ThemeProvider>
                                    <WorkspaceProvider>
                                        <VaultProvider>
                                            <SidebarProvider>
                                                <Toaster position="top-right" />
                                                <Router>
                                                    <AppRoutes />
                                                </Router>
                                            </SidebarProvider>
                                        </VaultProvider>
                                    </WorkspaceProvider>
                                </ThemeProvider>
                            </MaintenanceWrapper>
                        </MaintenanceProvider>
                    </AuthProvider>
                </OnboardingProvider>
            </PersistGate>
        </Provider>
    );
};

export default App;